import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, TextField } from '@mui/material';

const cellSx = {
  width: 48,
  '& .MuiOutlinedInput-root': {
    height: 48,
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
    fontSize: 18,
    fontWeight: 500,
    '& fieldset': {
      borderColor: '#D5DCE1',
    },
    '&:hover fieldset': {
      borderColor: '#148dfd',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#148dfd',
    },
  },
  '& .MuiOutlinedInput-input': {
    textAlign: 'center',
    padding: '10px 0',
  },
};

function SixDigitOtpInput({ value, onChange, length, autoFocus, disabled }) {
  const inputRefs = useRef([]);

  const digits = Array.from({ length }, (_, index) => value[index] || '');

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const updateValue = (nextDigits) => {
    onChange(nextDigits.join('').slice(0, length));
  };

  const focusInput = (index) => {
    const el = inputRefs.current[index];
    if (el) el.focus();
  };

  const handleChange = (index, raw) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    updateValue(next);
    if (digit && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const next = [...digits];
      if (digits[index]) {
        next[index] = '';
        updateValue(next);
        return;
      }
      if (index > 0) {
        next[index - 1] = '';
        updateValue(next);
        focusInput(index - 1);
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    onChange(pasted);
    const focusIndex = Math.min(pasted.length, length - 1);
    focusInput(focusIndex);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '16px',
        justifyContent: 'flex-start',
      }}
      onPaste={handlePaste}
    >
      {digits.map((digit, index) => (
        <TextField
          key={index}
          inputRef={(el) => {
            inputRefs.current[index] = el;
          }}
          value={digit}
          disabled={disabled}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onFocus={(event) => event.target.select()}
          inputProps={{
            inputMode: 'numeric',
            autoComplete: index === 0 ? 'one-time-code' : 'off',
            'aria-label': `Digit ${index + 1}`,
            maxLength: 1,
          }}
          sx={cellSx}
        />
      ))}
    </Box>
  );
}

SixDigitOtpInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  length: PropTypes.number,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
};

SixDigitOtpInput.defaultProps = {
  value: '',
  length: 6,
  autoFocus: false,
  disabled: false,
};

export default SixDigitOtpInput;
